// CSP first — installs the MutationObserver that stamps nonces on dynamically
// injected <style> tags. Must run before any import that could create one
// (react-toastify CSS-in-JS, react-datepicker, etc.). The strict CSP <meta>
// itself is injected after a delay so the initial module graph can finish
// loading without being blocked.
import { initCsp } from './lib/csp';
initCsp();

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { initErrorReporting } from './lib/errorReporting';
import { initTheme } from './lib/initTheme';
import { redirectFromLegacyHost } from './lib/hostBridge';
import { setMountConfig, MountMode } from './mountConfig';

// Phase E coexistence: if the standalone bundle is being served from the
// legacy AngularJS host (because Infra hasn't completed the proxy/DNS cutover
// yet for this tenant), bounce to the standalone host immediately. No-op for
// dev environments and when already on the standalone host.
if (redirectFromLegacyHost()) {
  // The page is about to navigate — skip the rest of the boot sequence so we
  // don't waste time mounting React on a doomed location.
  throw new Error('[ReactShell] Redirecting from legacy host — boot aborted.');
}

initErrorReporting();
initTheme();

// PKCE callback recovery (vendor flow).
//
// After Auth0 sign-in the PKCE server bounces the browser back to the app
// origin without a hash — e.g. `https://app.sgpdev.com/` with the Bearer
// cookie set. In embedded mode the AngularJS `app.run` block (see
// public/SPIGlassApp/app.js around line 183-188) detected
// `vc_auth_pending === 'vendor'` and routed to /VendorConnect/Dashboard.
// Standalone has no AngularJS to do this, so we rewrite the hash before
// HashRouter mounts. Without this, the router lands on /Home/dashboard,
// AuthGate fires the staff RoleMaster path, and vendors see "Authentication
// failed" because they don't carry the staff permissions.
try {
  if (sessionStorage.getItem('vc_auth_pending') === 'vendor') {
    const currentHash = window.location.hash || '';
    if (
      !currentHash.toLowerCase().includes('vendorconnect') &&
      !currentHash.includes('vendor-connect')
    ) {
      window.location.hash = '#/VendorConnect/Dashboard';
    }
  }
} catch {
  // sessionStorage unavailable (private mode / SSR) — no-op.
}

// When a new build is deployed, old cached index.js references chunk hashes that
// no longer exist on the server. Vite fires 'vite:preloadError' on these 404s.
// Force a full reload so the browser picks up the latest index.js and chunk manifest.
//
// LOOP GUARD: limit to one reload per tab session. Without this, a persistent
// preload failure (dev server restart while a tab is open, network glitch, a
// chunk that genuinely 404s on the server) sends the page into an infinite
// reload loop — every reload re-fires preloadError, which re-fires reload,
// and the URL bar appears to flicker between the bare origin and the target
// hash. After the first reload we fall back to logging + surfacing an error
// so the user sees a real failure instead of an apparently-frozen browser.
const RELOAD_GUARD_KEY = 'spi_preload_reload_fired';
window.addEventListener('vite:preloadError', (event) => {
  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
  } catch {
    // sessionStorage unavailable — degrade to "always reload once per page life"
    // which is still finite (the page would have to be navigated to fresh).
  }
  if (alreadyReloaded) {
    console.error(
      '[ReactShell] vite:preloadError fired again after a recovery reload — ' +
        'not reloading to avoid a loop. The underlying chunk is genuinely ' +
        'unavailable.',
      event
    );
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    // ignore — single-reload behavior just degrades
  }
  window.location.reload();
});

import type { FeatureFlags } from './config/featureFlags';
import type { LegacyPermissionKey } from './config/legacyPermissions';

const SCOPE_CLASS = 'react-dashboard-host';

type DashboardMountOptions = {
  container?: HTMLElement | null;
  containerId?: string;
  initialPath?: string;
  mode?: MountMode;
  featureFlags?: Partial<FeatureFlags>;
  permissions?: LegacyPermissionKey[];
};

let dashboardRoot: ReactDOM.Root | null = null;
let dashboardContainer: HTMLElement | null = null;

function renderApp(container: HTMLElement) {
  container.classList.add(SCOPE_CLASS);
  dashboardContainer = container;
  try {
    dashboardRoot = ReactDOM.createRoot(container);
    dashboardRoot.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } catch (err) {
    console.error('[ReactShell] renderApp: CRASHED:', err);
  }
}

function resolveContainer(options?: DashboardMountOptions) {
  if (options?.container) {
    return options.container;
  }

  const fallbackId = options?.containerId || 'react-dashboard-root';
  return document.getElementById(fallbackId);
}

function ensureUnmounted() {
  if (dashboardRoot) {
    dashboardRoot.unmount();
    dashboardRoot = null;
  }

  if (dashboardContainer) {
    dashboardContainer.classList.remove(SCOPE_CLASS);
  }

  dashboardContainer = null;
}

export function mountReactDashboard(options?: DashboardMountOptions) {
  const container = resolveContainer(options);

  if (!container) {
    console.error('React dashboard mount failed: container not found.', options);
    return false;
  }

  const mountMode: MountMode = options?.mode ?? 'embedded';
  const initialPath = options?.initialPath ?? '/Home/dashboard';
  // Permission overrides: only use explicitly passed permissions (e.g. VendorConnect).
  // In standalone mode, permissions are fetched from the API via LegacyPermissionsProvider
  // and AuthGate (initAuth → fetchAndResolvePermissions). No dev-mode override needed
  // since the Vite proxy handles auth.
  const resolvedPermissions = options?.permissions;

  setMountConfig({
    mode: mountMode,
    initialPath,
    featureFlagOverrides: options?.featureFlags,
    permissionOverrides: resolvedPermissions,
  });

  ensureUnmounted();
  renderApp(container);
  return true;
}

export function unmountReactDashboard() {
  if (!dashboardRoot) {
    return;
  }

  ensureUnmounted();
}

declare global {
  interface Window {
    mountReactDashboard?: typeof mountReactDashboard;
    unmountReactDashboard?: typeof unmountReactDashboard;
  }
}

window.mountReactDashboard = mountReactDashboard;
window.unmountReactDashboard = unmountReactDashboard;

// Auto-mount in standalone mode (when loaded via index.html directly)
const standaloneHost = document.getElementById('root');

if (standaloneHost) {
  mountReactDashboard({
    container: standaloneHost,
    mode: 'standalone',
    initialPath: window.location.pathname,
  });
}
